import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { closeDetails, deleteUniversalLicense, revokeLicense, updateUniversalLicense } from "@dashboard/devices/store/index";
import { License } from "@dashboard/devices/types/index";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getNestedGroupsWithIndentation } from "@dashboard/groups/utils";
import { featureOn } from "@dashboard/shared/limits/FeatureTile";
import { getFeature } from "@dashboard/shared/limits/utils";
import { useCurrentWorkspace, useCurrentWorkspaceFeatures } from "@dashboard/workspaces/api/helpers";
import { Box, TextField, Theme, Typography } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { DangerButton, DangerOutlinedButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SelectOption } from "@shared/form/SelectField/SelectField.types";
import { LoadingIcon } from "@shared/Loading";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { PaneElement } from "@sidePanel/shared/PaneElement";

const useStyles = makeStyles()((theme: Theme) => ({
    revokeWarning: {
        color: theme.palette.emteria.error,
    },
    paragraph: { marginBottom: theme.spacing(2) },
    label: { marginBottom: theme.spacing(1), marginTop: theme.spacing(1) },
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
    btnBox: { display: "flex", gap: theme.spacing(1), alignSelf: "flex-end" },
    revokeSectionLoader: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: theme.spacing(12),
    },
    reasonTextField: { marginTop: theme.spacing(2) },
}));

interface FormValues {
    groupId: number | null;
}
const LicenseTab = (license: License) => {
    const [openRevokeDialog, setOpenRevokeDialog] = React.useState(false);
    const [isRemoving, setIsRemoving] = React.useState(false);
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [isRevoking, setIsRevoking] = React.useState(false);
    const [reason, setReason] = React.useState("");

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["sidepanels", "common", "modals"]);

    const { data: workspace } = useCurrentWorkspace();
    const { data: workspaceFeatures, isLoading: isWorkspaceFeaturesLoading } = useCurrentWorkspaceFeatures();

    const permission = PermissionService();
    const canManageLicense = permission.licenseAbility(license).can("manage", "License");

    const { max: licenseRevocationMax } = getFeature("LicenseRevocation", workspaceFeatures ?? []);
    const LicenseRevocationEnabled = licenseRevocationMax === featureOn;
    const isUniversal = Boolean(license.universalGroupName);

    const groups = useSelector(selectGroups);
    const accessibleGroups = groups.filter((item) => permission.groupAbility(item, workspace).can("manage", "UniversalLicense"));
    const groupOptions = getNestedGroupsWithIndentation(accessibleGroups);

    const avaibleGroups = groups.filter((item) => permission.groupAbility(item, workspace).can("manage", "UniversalLicense"));
    const isGroupIdInputDisabled = !avaibleGroups.length || !canManageLicense || isRemoving || isUpdating;

    const isReasonTooShort = reason.trim().length < 10;

    const onRevoke = async () => {
        try {
            setIsRevoking(true);
            await dispatch(revokeLicense({ licenseId: license.id, reason: reason.trim() })).unwrap();
            showSuccessToast(t("sidepanels:devicePanel.toast.licenseRevokedSuccess"));
            dispatch(closeDetails());
            setOpenRevokeDialog(false);
            setReason("");
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
            return;
        } finally {
            setIsRevoking(false);
        }
    };
    const onSubmit = async (values: FormValues) => {
        setIsUpdating(true);
        await dispatch(updateUniversalLicense({ id: license.id, groupId: Number(values.groupId) }))
            .unwrap()
            .then(() => showSuccessToast(t("sidepanels:devicePanel.toast.targetGroupChangedSuccess")))
            .catch(({ message = t("sidepanels:devicePanel.toast.targetGroupChangeFailed") }) => showErrorToast(message))
            .finally(() => setIsUpdating(false));
    };
    const onRemove = async (form: FormApi<FormValues>) => {
        if (!isUniversal) {
            form.reset({ groupId: license.universalGroupId });
        } else {
            setIsRemoving(true);
            await dispatch(deleteUniversalLicense(license.id))
                .unwrap()
                .then(() => showSuccessToast(t("sidepanels:devicePanel.toast.licenseNotUniversal")))
                .catch(({ message = t("sidepanels:devicePanel.toast.removeUniversalLicenseFailed") }) => showErrorToast(message))
                .finally(() => setIsRemoving(false));
        }
    };
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !isRevoking && !isReasonTooShort) {
            await onRevoke();
        }
    };
    const onGroupChange = async (selectedOption: SelectOption | null, form: FormApi<FormValues, Partial<FormValues>>) => {
        if (!selectedOption) {
            await onRemove(form);
            return;
        }
        if (typeof selectedOption.value !== "number") {
            return;
        }

        await onSubmit({ groupId: selectedOption.value });
    };
    const getTooltipTitle = () => {
        if (!canManageLicense) {
            return t("sidepanels:devicePanel.tooltip.noPermissionToChangeTargetGroup");
        }
        if (!avaibleGroups.length) {
            return t("sidepanels:devicePanel.tooltip.noGroupsAvailable");
        }
        return "";
    };

    return (
        <PaneElement.Container>
            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={{ groupId: license.universalGroupId || undefined }}
                render={({ handleSubmit, values, form }) => (
                    <React.Fragment>
                        <PaneElement.Section>
                            <PaneElement.Header>{t("sidepanels:devicePanel.licenseTab.revokeLicense")}</PaneElement.Header>
                            {isWorkspaceFeaturesLoading ? (
                                <Box className={classes.revokeSectionLoader}>
                                    <LoadingIcon size="block" />
                                </Box>
                            ) : (
                                <React.Fragment>
                                    <Typography className={classes.paragraph}>
                                        {!LicenseRevocationEnabled ? (
                                            <Trans
                                                ns="sidepanels"
                                                i18nKey="devicePanel.licenseTab.revokeLicenseDisabled"
                                                components={{
                                                    CustomLink: <a href="mailto:support@emteria.com" />,
                                                }}
                                                values={{ contact: "support@emteria.com" }}
                                            />
                                        ) : (
                                            t("sidepanels:devicePanel.licenseTab.description")
                                        )}
                                    </Typography>
                                    {LicenseRevocationEnabled ? (
                                        <div className={classes.btnBox}>
                                            <DangerOutlinedButton
                                                onClick={() => setOpenRevokeDialog(true)}
                                                disabled={!canManageLicense}
                                                tooltipProps={{
                                                    hide: canManageLicense,
                                                    title: !canManageLicense
                                                        ? t("sidepanels:devicePanel.tooltip.noPermissionToRevokeLicense")
                                                        : t("sidepanels:devicePanel.tooltip.licenseAlreadyRevoked"),
                                                }}
                                            >
                                                {t("common:button.revoke")}
                                            </DangerOutlinedButton>
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            )}
                        </PaneElement.Section>

                        <PaneElement.Section withoutBorder>
                            <PaneElement.Header>{t("sidepanels:devicePanel.licenseTab.universalStatus")}</PaneElement.Header>
                            <Typography className={classes.paragraph}>
                                <Trans
                                    ns="sidepanels"
                                    i18nKey="devicePanel.licenseTab.universalLicenseDescription"
                                    components={{
                                        // Deliberately not named "link": html-parse-stringify treats
                                        // <link> as a void element, so Trans would drop the label out
                                        // of the anchor and leave it unclickable.
                                        licenseLink: (
                                            <a
                                                href="https://emteria.com/kb/create-universal-license#ul"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            />
                                        ),
                                    }}
                                />
                            </Typography>
                            <form onSubmit={handleSubmit}>
                                <Box className={classes.form}>
                                    <Tooltip title={getTooltipTitle()} hide={!!avaibleGroups.length && canManageLicense}>
                                        <SelectField
                                            value={groupOptions.find((option) => option.value === values?.groupId)}
                                            onChange={(_event, selectedOption) => onGroupChange(selectedOption, form)}
                                            options={groupOptions}
                                            disabled={isGroupIdInputDisabled}
                                            disableClearable={false}
                                            placeholder={t("sidepanels:devicePanel.licenseTab.notUniversalPlaceholder")}
                                            fullWidth
                                            disableSorting
                                        />
                                    </Tooltip>
                                </Box>
                            </form>
                        </PaneElement.Section>

                        <Modal
                            title={t("modals:revokeLicense.title")}
                            open={openRevokeDialog}
                            onClose={() => {
                                setOpenRevokeDialog(false);
                                setReason("");
                            }}
                            onKeyDown={onKeyDown}
                            description={
                                <Trans
                                    ns="modals"
                                    i18nKey="revokeLicense.description"
                                    components={{
                                        bold: <strong />,
                                        br: <br />,
                                        red: <strong className={classes.revokeWarning} />,
                                    }}
                                />
                            }
                            endButton={
                                <DangerButton onClick={onRevoke} loading={isRevoking} disabled={isRevoking || isReasonTooShort}>
                                    {t("common:button.revoke")}
                                </DangerButton>
                            }
                        >
                            <TextField
                                label={t("common:label.reason")}
                                placeholder={t("modals:revokeLicense.reason.placeholder")}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                                required
                                disabled={isRevoking}
                                autoFocus
                                className={classes.reasonTextField}
                                error={reason.length > 0 && isReasonTooShort}
                                helperText={
                                    reason.length > 0 && isReasonTooShort ? t("modals:revokeLicense.reason.tooShort") : undefined
                                }
                            />
                        </Modal>
                    </React.Fragment>
                )}
            />
        </PaneElement.Container>
    );
};
export default LicenseTab;
